import app from "../../../global-account/app.js";
import { useAuthStore, useB3Config } from "../../../global-account/react/index.js";
import { debugB3React } from "../../../shared/utils/debug.js";
import { useCallback } from "react";
import { getBetterAuthClient } from "../../better-auth-client.js";
import { useUserQuery } from "./useUserQuery.js";
const debug = debugB3React("useBetterAuth");
/** Thrown when email verification is required before the user can sign in. */
export class EmailVerificationRequiredError extends Error {
  constructor(message = "Check your email to verify your account before signing in.") {
    super(message);
    this.name = "EmailVerificationRequiredError";
  }
}
/**
 * Hook for authentication via Better Auth (email/password + social providers).
 *
 * Follows the same pattern as Thirdweb auth:
 * 1. Better Auth sign-in returns a session token
 * 2. Token is passed as accessToken to app.authenticate({ strategy: "better-auth" })
 * 3. BetterAuthStrategy verifies the token and issues a Feathers JWT
 */
export function useBetterAuth() {
  const {
    partnerId
  } = useB3Config();
  const {
    setUser
  } = useUserQuery();
  const setIsAuthenticated = useAuthStore(state => state.setIsAuthenticated);
  const setIsAuthenticating = useAuthStore(state_0 => state_0.setIsAuthenticating);
  const setIsConnected = useAuthStore(state_1 => state_1.setIsConnected);
  const setHasStartedConnecting = useAuthStore(state_2 => state_2.setHasStartedConnecting);
  const exchangeForFeathersJWT = useCallback(async sessionToken => {
    debug("Exchanging Better Auth token for Feathers JWT");
    const response = await app.authenticate({
      strategy: "better-auth",
      accessToken: sessionToken,
      partnerId
    });
    if (!response.user) {
      throw new Error("No user returned from authentication");
    }
    setUser(response.user);
    setIsAuthenticated(true);
    setIsConnected(true);
    setIsAuthenticating(false);
    debug("Feathers JWT exchange successful", {
      userId: response.user?.userId
    });
    return response;
  }, [partnerId, setUser, setIsAuthenticated, setIsConnected, setIsAuthenticating]);
  const signInWithEmail = useCallback(async (email, password) => {
    debug("Signing in with email", {
      email
    });
    setHasStartedConnecting(true);
    setIsAuthenticating(true);
    try {
      const result = await getBetterAuthClient().signIn.email({
        email,
        password
      });
      if (result.error) {
        throw new Error(result.error.message || "Sign in failed");
      }
      const token = result.data?.token;
      if (!token) {
        throw new Error("No session token returned from sign in");
      }
      return await exchangeForFeathersJWT(token);
    } catch (error) {
      setIsAuthenticating(false);
      debug("Sign in failed", error);
      throw error;
    }
  }, [exchangeForFeathersJWT, setIsAuthenticating, setHasStartedConnecting]);
  const signUpWithEmail = useCallback(async (email_0, password_0, name, verifyCallbackURL) => {
    debug("Signing up with email", {
      email: email_0,
      name
    });
    setHasStartedConnecting(true);
    setIsAuthenticating(true);
    try {
      const result_0 = await getBetterAuthClient().signUp.email({
        email: email_0,
        password: password_0,
        name
      });
      if (result_0.error) {
        throw new Error(result_0.error.message || "Sign up failed");
      }
      const token_0 = result_0.data?.token;
      if (!token_0) {
        // requireEmailVerification is enabled — send verification email with
        // a callbackURL Better Auth redirects to after server-side verify.
        // Pass verifyCallbackURL to land on a dedicated confirmation page.
        await getBetterAuthClient().sendVerificationEmail({
          email: email_0,
          callbackURL: verifyCallbackURL || `${window.location.origin}?authStrategy=better-auth`
        });
        throw new EmailVerificationRequiredError();
      }
      return await exchangeForFeathersJWT(token_0);
    } catch (error_0) {
      setIsAuthenticating(false);
      debug("Sign up failed", error_0);
      throw error_0;
    }
  }, [exchangeForFeathersJWT, setIsAuthenticating, setHasStartedConnecting]);
  const signInWithSocial = useCallback(async provider => {
    debug("Signing in with social provider", {
      provider
    });
    setHasStartedConnecting(true);
    setIsAuthenticating(true);
    try {
      const result_1 = await getBetterAuthClient().signIn.social({
        provider,
        callbackURL: window.location.href
      });
      if (result_1.error) {
        throw new Error(result_1.error.message || `${provider} sign in failed`);
      }
      // Social auth redirects to provider — JWT exchange happens on callback
      // via BetterAuthProvider session restore
    } catch (error_1) {
      setIsAuthenticating(false);
      debug("Social sign in failed", error_1);
      throw error_1;
    }
  }, [setIsAuthenticating, setHasStartedConnecting]);
  const requestPasswordReset = useCallback(async (email_1, redirectTo) => {
    debug("Requesting password reset", {
      email: email_1
    });
    const result_2 = await getBetterAuthClient().requestPasswordReset({
      email: email_1,
      redirectTo
    });
    if (result_2.error) {
      throw new Error(result_2.error.message || "Password reset request failed");
    }
    debug("Password reset email sent");
    return result_2;
  }, []);
  const resetPassword = useCallback(async (newPassword, token_1) => {
    debug("Resetting password");
    const result_3 = await getBetterAuthClient().resetPassword({
      newPassword,
      token: token_1
    });
    if (result_3.error) {
      throw new Error(result_3.error.message || "Password reset failed");
    }
    debug("Password reset successful");
    return result_3;
  }, []);
  return {
    signInWithEmail,
    signUpWithEmail,
    signInWithSocial,
    requestPasswordReset,
    resetPassword,
    exchangeForFeathersJWT,
    betterAuthClient: getBetterAuthClient()
  };
}